import { queueStatus } from '~/enums/queueStatus'
import { ROLE_LEADER, ROLE_CO_LEADER, ROLE_CAPTAIN } from '~/utils/constants'
import { DateTime } from 'luxon'
import { RegistrationStatus } from '~/enums/registrationStatus'

const defaultState = () => {
	return {
		user: null,
		userId: null,
		usThLevel: null,
		visitorCountry: null,
		registrationAllowed: false,
		token: {},
		gameAccounts: [],
		roster: null,
		recentlyJoinedRoster: false,
		isLeader: false,
		isCoLeader: false,
		clan: null,
		latestRoster: null,
		rosters: [],
		middlewareRosterId: null,
		initialLoadErrorMessage: null,
		clanMembers: [],
		clanRoster: null,
		queueStatus: queueStatus.NONE,
		showSegmentCheckInModal: false,
	}
}

const calculateLatestRoster = (rosters) => {
	const sortedRoster = [...rosters].sort((a, b) => {
		// greatest tree level first, that means the most recent roster
		if (a.tree?.level !== undefined && b.tree?.level !== undefined) {
			if (a.tree.level > b.tree.level) {
				return -1
			}
			if (a.tree.level < b.tree.level) {
				return 1
			}
		}
		// it's a fallback in case there isn't in tree
		const dtA = DateTime.fromISO(a.createdAt).toMillis()
		const dtB = DateTime.fromISO(b.createdAt).toMillis()
		if (dtA > dtB) {
			return -1
		}
		if (dtA < dtB) {
			return 1
		}
		return 0
	})

	if (sortedRoster.length) {
		return sortedRoster[0]
	}
	if (rosters.length) {
		return rosters[0]
	}
	return null
}

export const state = () => defaultState()

export const mutations = {
	setUser(state, user) {
		state.user = user
	},
	setUserId(state, userId) {
		state.userId = userId
	},
	setUsThLevel(state, usThLevel) {
		state.usThLevel = usThLevel
	},
	setToken(state, token) {
		state.token = token
	},
	setLatestRoster(state, latestRoster) {
		state.latestRoster = latestRoster
	},
	setRosters(state, rosters) {
		state.rosters = rosters
	},
	setDefaultState(state) {
		Object.assign(state, defaultState())
	},
	setRegistrationAllowed(state, isRegistrationAllowed) {
		state.registrationAllowed = isRegistrationAllowed
	},
	setRecentlyJoinedRoster(state, value) {
		state.recentlyJoinedRoster = value
	},
	setIsLeader(state, isLeader) {
		state.isLeader = isLeader
	},
	setIsCoLeader(state, isCoLeader) {
		state.isCoLeader = isCoLeader
	},
	setClan(state, clan) {
		state.clan = clan
	},
	setMiddlewareRosterId(state, middlewareRosterId) {
		state.middlewareRosterId = middlewareRosterId
	},
	setInitialLoadErrorMessage(state, initialLoadErrorMessage) {
		state.initialLoadErrorMessage = initialLoadErrorMessage
	},
	setClanMembers(state, clanMembers) {
		state.clanMembers = clanMembers
	},
	setClanRoster(state, clanRoster) {
		state.clanRoster = clanRoster
	},
	setQueueStatus(state, status) {
		state.queueStatus = status
	},
	setShowSegmentCheckInModal(state, value) {
		state.showSegmentCheckInModal = value
	},
}

export const actions = {
	resetRoster({ commit }) {
		commit('setRosters', [])
		commit('setLatestRoster', null)
		commit('setMiddlewareRosterId', null)
	},

	async getRoster({ commit, dispatch }, { userId, eventId, middlewareRosterId }) {
		try {
			if (userId && eventId) {
				const { data: roster } = await this.$api.rosters.retrieve.get({
					'filter[event]': eventId,
					'filter[user]': userId,
				})

				if (roster?.length) {
					const latestRoster = calculateLatestRoster(roster)
					commit('setRosters', roster)
					commit('setLatestRoster', latestRoster)

					return
				}
			}

			if (middlewareRosterId) {
				const { data: rosterData } = await this.$api.rosters.retrieve.getRosterById(
					middlewareRosterId
				)

				commit('setRosters', [rosterData])
				commit('setLatestRoster', rosterData)

				return
			}

			commit('setRosters', [])
			commit('setLatestRoster', null)

			throw new Error(
				`no userId/eventId or middlewareRosterId set: ${userId}, ${eventId}, ${middlewareRosterId}`
			)
		} catch (err) {
			dispatch('error/handleError', err, { root: true })
			await this.$logApi.createLogMessage(`getRoster failed`, {
				error: JSON.stringify(err),
			})
		}
	},
}

export const getters = {
	hasRoster(state) {
		return !!state.latestRoster
	},
	rosterMembers(state) {
		return state.clanMembers.filter((m) => m.isRosterMember)
	},
	registeredPlayersAmount(state) {
		return state.clanMembers.filter((m) => m.isRosterMember && m.userId).length
	},
	hasRosterCaptainSelected(state) {
		return state.clanMembers.some((m) => m.isRosterMember && m.rosterPosition === ROLE_CAPTAIN)
	},
	isClanCaptain(state, getters, rootState) {
		return state.clanMembers.some(
			(m) =>
				m.isRosterMember &&
				m.rosterPosition === ROLE_CAPTAIN &&
				m.tag === rootState.playerTag
		)
	},
	captainCorrectRole(state, getters) {
		if (getters.hasRosterCaptainSelected) {
			return state.clanMembers.some(
				(m) =>
					m.isRosterMember &&
					m.rosterPosition === ROLE_CAPTAIN &&
					(m.role === ROLE_CO_LEADER || m.role === ROLE_LEADER)
			)
		}
	},
	rosterLocked(state) {
		return !!state.clanRoster?.confirmed_at
	},
	canManageTeam(state, getters, rootState) {
		return Boolean(
			rootState.event.registrationStatus === RegistrationStatus.OPEN &&
				(state.isLeader || (state.isCoLeader && getters.isClanCaptain)) &&
				!getters.rosterLocked &&
				state.middlewareRosterId
		)
	},
}
